import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar, APP } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import { formatVnd } from "../../../utils/financeFormat";
import { getBdcPeriodListApi } from "../../../api/finance.bdc.api";

const TEXT = "#18212F";
const MUTED = "#94A3B8";
const BORDER = "#E5E9F0";
const WARNING = "#F79D0D";
const LIMIT = 10;

const SumRow = ({ label, value, color }) => (
  <Flex justify="space-between" gap="12px">
    <Text fontSize="13px" color={MUTED}>{label}</Text>
    <Text fontSize="13px" fontWeight={600} color={color || TEXT} textAlign="right">{value}</Text>
  </Flex>
);

const PeriodCard = ({ item }) => {
  const sourcePdv = !!item?.supersededByPdv;
  return (
    <Box bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
      <Flex align="center" justify="space-between" gap="8px">
        <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">{item?.apartmentCode || "—"}</Text>
        <Box bg="#F1F4F9" borderRadius="6px" px="8px" py="2px">
          <Text fontSize="13px" fontWeight={500} color={sourcePdv ? APP.primary200 : WARNING}>
            {sourcePdv ? "Tính toán PDV" : "Import CCMG"}
          </Text>
        </Box>
      </Flex>
      <Box h="1px" bg={BORDER} my="12px" />
      <Flex direction="column" gap="8px">
        <SumRow label="Đợt" value={item?.period != null ? `Đợt ${item.period}` : "—"} />
        <SumRow label="Tháng/Năm" value={item?.monthYear || "—"} />
        <SumRow label="Số tiền CCMG" value={formatVnd(item?.amountHeld || 0)} color={APP.primary200} />
      </Flex>
    </Box>
  );
};

const CcmgHeldDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const name = search.get("name") || "";
  const code = search.get("code") || "";

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (p, mode) => {
      if (!id) return;
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getBdcPeriodListApi({ employeeId: id, page: p, limit: LIMIT });
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  const pageBody = (
    <>
      <Box px="16px" pt="12px">
        {(name || code) && (
          <Text fontSize="14px" fontWeight={600} color={TEXT} mb="12px">
            {name}
            {code ? ` (${code})` : ""}
          </Text>
        )}
        {loading && !items.length ? (
          <Flex justify="center" py="32px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="12px" color="#808CA0" textAlign="center" py="32px">Chưa có dữ liệu lịch sử CCMG</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it, i) => (
              <PeriodCard key={it._id || i} item={it} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Tạm giữ CCMG" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Tạm giữ CCMG" />
      {pageBody}
    </Box>
  );
};

export default CcmgHeldDetail;
